import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { Box, FormControl, MenuItem, MenuList, MenuListProps, Select, SelectChangeEvent, SxProps, Theme } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import type { VirtuosoHandle } from 'react-virtuoso';
import { ReactComponent as ArrowDownIcon } from '../static/images/svg/iconamoon_arrow-up-2-fill.svg';

/** 药丸下拉虚拟列表：单行高度与 MUI MenuItem（fontSize 16 + padding）对齐 */
const PILL_MENU_VIRTUAL_ITEM_PX = 48;
const PILL_MENU_VIRTUAL_MAX_HEIGHT_PX = 320;

/**
 * MUI Select 下拉：用 Virtuoso 只挂载可视区 MenuItem，避免上万条选项时 DOM 过大
 */
const VirtualizedPillSelectMenuList = React.forwardRef<HTMLDivElement, MenuListProps>(
    function VirtualizedPillSelectMenuList(props, ref) {
        const { children, sx, ...other } = props;
        const items = React.Children.toArray(children) as React.ReactElement[];
        const count = items.length;
        const height = Math.min(
            PILL_MENU_VIRTUAL_MAX_HEIGHT_PX,
            Math.max(count * PILL_MENU_VIRTUAL_ITEM_PX, PILL_MENU_VIRTUAL_ITEM_PX),
        );

        const virtuosoRef = useRef<VirtuosoHandle>(null);
        const menuListRootRef = useRef<HTMLDivElement | null>(null);

        /** MUI Menu 的 Grow 动画结束后视口高度才稳定；首帧测高偏小会导致只渲染上半屏，滚动后才触发 ResizeObserver 正常 */
        const bumpVirtuosoViewport = React.useCallback(() => {
            virtuosoRef.current?.scrollTo({ top: 0 });
        }, []);

        useLayoutEffect(() => {
            let cancelled = false;
            let id2 = 0;
            const id1 = requestAnimationFrame(() => {
                id2 = requestAnimationFrame(() => {
                    if (!cancelled) {
                        bumpVirtuosoViewport();
                    }
                });
            });
            return () => {
                cancelled = true;
                cancelAnimationFrame(id1);
                cancelAnimationFrame(id2);
            };
        }, [count, bumpVirtuosoViewport]);

        const setMergedRef = React.useCallback(
            (node: HTMLDivElement | null) => {
                menuListRootRef.current = node;
                if (typeof ref === 'function') {
                    ref(node);
                } else if (ref) {
                    (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
                }
            },
            [ref],
        );

        return (
            <MenuList
                ref={setMergedRef}
                component="div"
                {...other}
                sx={[
                    {
                        p: 0,
                        maxHeight: PILL_MENU_VIRTUAL_MAX_HEIGHT_PX,
                        overflow: 'hidden',
                        /** 给 Virtuoso 明确块级高度，避免与 Popper 动画叠加时子节点高度为 0 */
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                    },
                    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
                ]}
            >
                <Virtuoso
                    ref={virtuosoRef}
                    style={{ height, flex: '1 1 auto', minHeight: 0 }}
                    totalCount={count}
                    fixedItemHeight={PILL_MENU_VIRTUAL_ITEM_PX}
                    defaultItemHeight={PILL_MENU_VIRTUAL_ITEM_PX}
                    increaseViewportBy={{ top: 80, bottom: 120 }}
                    itemContent={(index) => items[index] ?? null}
                />
            </MenuList>
        );
    },
);
VirtualizedPillSelectMenuList.displayName = 'VirtualizedPillSelectMenuList';

const PILL_SELECT_CHEVRON_WRAPPER_SX = {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--icon-primary-300)',
    pointerEvents: 'none',
} as const;

/** 下拉箭头：默认 iconamoon，与 Figma 一致 */
const PillSelectChevron = React.forwardRef<HTMLSpanElement>((_props, ref) => (
    <Box ref={ref} component="span" sx={PILL_SELECT_CHEVRON_WRAPPER_SX}>
        <ArrowDownIcon style={{ width: 24, height: 24 }} />
    </Box>
));
PillSelectChevron.displayName = 'PillSelectChevron';

/** 使用调用方传入的图标组件（如 Tabler `chevron-down`）作为 MUI Select 的 IconComponent */
function createPillSelectChevron(Icon: React.ElementType) {
    const Cmp = React.forwardRef<HTMLSpanElement>((_props, ref) => (
        <Box ref={ref} component="span" sx={PILL_SELECT_CHEVRON_WRAPPER_SX}>
            <Icon style={{ width: 24, height: 24 }} />
        </Box>
    ));
    Cmp.displayName = 'PillSelectChevronCustom';
    return Cmp;
}

/** 下拉选项：value 为选中值，label 为展示名（不含前缀） */
export interface PrefixLabeledPillSelectOption {
    /** 选项唯一值，对应 Select 的 value */
    value: string;
    /** 展示名称（前缀由 labelPrefix 统一拼接） */
    label: string;
}

export interface PrefixLabeledPillSelectProps {
    /** 选项列表 */
    options: PrefixLabeledPillSelectOption[];
    /** 当前选中的 value */
    value: string;
    /** 选中变更回调 */
    onChange: (nextValue: string) => void;
    /** 展示在名称前的附属标签（如「集团-」，由调用方传入） */
    labelPrefix?: string;
    /** 左侧图标区域，由调用方传入（如门店 SVG）；不传则不占位 */
    leadingIcon?: React.ReactNode;
    /** 大小，默认 medium */
    size?: 'small' | 'medium';
    sx?: SxProps<Theme>;
    /**
     * 为 true 时下拉列表使用虚拟滚动（react-virtuoso），适合大量选项
     * （键盘在部分 MUI 版本下对未挂载项可能不完整，大数据场景以可滚动性优先）
     */
    virtualizeMenu?: boolean;
    /**
     * 右侧下拉箭头图标（`ReactComponent`），例如 `tabler_chevron-down.svg`。
     * 不传则使用内置 iconamoon 箭头。
     */
    dropdownIconComponent?: React.ElementType;
    /** 根节点 `Box` 的 className（便于页面级样式） */
    className?: string;
}

/**
 * 带前缀文案的圆角药丸下拉（可选 leadingIcon + 前缀 + 名称 + 箭头）。
 * 可用于集团、周期等场景，选项与图标由调用方传入。
 */
const PrefixLabeledPillSelect: React.FC<PrefixLabeledPillSelectProps> = ({
    options,
    value,
    onChange,
    labelPrefix,
    leadingIcon,
    size,
    sx,
    virtualizeMenu = false,
    dropdownIconComponent,
    className,
}) => {
    const SelectIconComponent = useMemo(
        () => (dropdownIconComponent ? createPillSelectChevron(dropdownIconComponent) : PillSelectChevron),
        [dropdownIconComponent],
    );

    if (!options.length) {
        return null;
    }
    const selected = options.find((o) => o.value === value);
    const displayName = selected?.label ?? '';

    const handleChange = (e: SelectChangeEvent<string>) => {
        onChange(String(e.target.value));
    };

    /** 默认外层 200×200min×360max；若根级只传 `width` 而不传 `minWidth`，合并后仍会残留默认 `minWidth:200`，故检测到根级 `width` 时不再套默认宽高 */
    const defaultRootSx = { width: 200, minWidth: 200, maxWidth: 360 };
    const sxParts: SxProps<Theme>[] = sx ? (Array.isArray(sx) ? sx : [sx]) : [];
    const sxHasThemeFn = sxParts.some((part) => typeof part === 'function');
    const sxHasRootWidth = sxParts.some((part) => {
        if (part == null || typeof part !== 'object' || Array.isArray(part)) {
            return false;
        }
        const w = (part as { width?: unknown }).width;
        return w !== undefined && w !== null;
    });
    const rootSx = (
        sxHasThemeFn || !sxHasRootWidth ? [defaultRootSx, ...sxParts] : sxParts
    ) as SxProps<Theme>;

    return (
        <Box sx={rootSx} className={className}>
            <FormControl variant="standard" sx={{ m: 0, width: '100%', minWidth: 0 }}>
            <Select
                value={value}
                onChange={handleChange}
                disableUnderline
                IconComponent={SelectIconComponent}
                /** 允许选中 value 为 `''` 的「无」项，否则框内空白不渲染 */
                displayEmpty

                renderValue={() => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: leadingIcon != null ? 1 : 0,
                            width: '100%',
                            minWidth: 0,
                            overflow: 'hidden',
                        }}
                    >
                        {leadingIcon != null ? (
                            <Box
                                component="span"
                                sx={{
                                    display: 'flex',
                                    flexShrink: 0,
                                    width: 32,
                                    height: 32,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'var(--icon-primary-300)',
                                }}
                            >
                                {leadingIcon}
                            </Box>
                        ) : null}
                        <Box
                            component="span"
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 20,
                                fontWeight: 500,
                                lineHeight: '24px',
                                color: 'var(--text-dark)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {labelPrefix}
                            {displayName}
                        </Box>
                    </Box>
                )}
                sx={{
                    borderRadius: size === 'small' ? '8px' : '16px',
                    bgcolor: 'var(--fill-neutral-100)',
                    boxShadow: '0px 0px 2px var(--shadow-50)',
                    px: 2,
                    minHeight: size === 'small' ? 24 : 48,
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '100%',
                    position: 'relative',
                    /** 去掉 MUI Select 聚焦时的灰底高亮 */
                    '&.MuiInputBase-root.Mui-focused': {
                        bgcolor: 'var(--fill-neutral-100)',
                    },
                    '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        py: 1,
                        /** 右侧留白：箭头 absolute right:12 + 宽 24，避免文字与图标重叠 */
                        pr: '24px !important',
                        minWidth: 0,
                        bgcolor: 'var(--fill-neutral-100)',
                        borderRadius: size === 'small' ? '8px' : '16px',
                        '&:focus, &:focus-visible': {
                            bgcolor: 'var(--fill-neutral-100)',
                        },
                    },
                    '&::before, &::after': { display: 'none' },
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            borderRadius: 2,
                            mt: 0.5,
                            boxShadow: '0px 4px 12px var(--shadow-25)',
                            bgcolor: 'var(--fill-neutral-100)',
                            ...(virtualizeMenu
                                ? {
                                      maxHeight: PILL_MENU_VIRTUAL_MAX_HEIGHT_PX,
                                      overflow: 'hidden',
                                      display: 'flex',
                                      flexDirection: 'column',
                                  }
                                : {}),
                        },
                    },
                    ...(virtualizeMenu
                        ? {
                              /** 关闭展开动画，避免 Grow 过程中 Virtuoso 视口高度测成「半高」 */
                              transitionDuration: 0,
                              MenuListProps: {
                                  component: VirtualizedPillSelectMenuList,
                              },
                          }
                        : {}),
                }}
            >
                {options.map((opt) => (
                    <MenuItem
                        key={opt.value === '' ? '__empty__' : opt.value}
                        value={opt.value}
                        sx={{ fontSize: 16, color: 'var(--text-dark)' }}
                    >
                        {opt.label}
                    </MenuItem>
                ))}
            </Select>
            </FormControl>
        </Box>
    );
};

export default PrefixLabeledPillSelect;
